// ============================================
// CustomPass - Base class for custom render passes
// ============================================

import type { IRenderContext } from '../core/IRenderContext';
import type { Camera2D } from '../objects/Camera2D';
import type { RenderTargetHandle, TextureHandle } from '../types';

/**
 * Injection points in the render pipeline where custom passes can execute
 */
export enum InjectionPoint {
  /** Before any rendering begins (after clear) */
  BeforeRendering = 0,
  /** Before opaque objects are rendered */
  BeforeOpaques = 1,
  /** After opaque objects, before transparent */
  AfterOpaques = 2,
  /** Before transparent objects are rendered */
  BeforeTransparents = 3,
  /** After transparent objects are rendered */
  AfterTransparents = 4,
  /** Before post-processing begins */
  BeforePostProcess = 5,
  /** After post-processing completes */
  AfterPostProcess = 6,
  /** Final pass before presenting to screen */
  BeforePresent = 7,
}

/**
 * Context passed to custom passes during execution
 */
export interface CustomPassContext {
  /** Render context for GPU operations */
  ctx: IRenderContext;
  /** Current camera */
  camera: Camera2D;
  /** Current render target (null = backbuffer) */
  renderTarget: RenderTargetHandle | null;
  /** Color buffer texture from current render target */
  colorTexture: TextureHandle | null;
  /** Depth buffer texture (if available) */
  depthTexture: TextureHandle | null;
  /** Viewport dimensions */
  width: number;
  height: number;
  /** Time since start (seconds) */
  time: number;
  /** Delta time (seconds) */
  deltaTime: number;
}

/**
 * Base class for custom render passes.
 * 
 * Extend this class to inject custom rendering at specific points
 * in the render pipeline.
 * 
 * @example
 * ```typescript
 * class MyCustomPass extends CustomPass {
 *   constructor() {
 *     super('MyPass', InjectionPoint.AfterTransparents);
 *   }
 *   
 *   protected onExecute(context: CustomPassContext): void {
 *     // Custom rendering code
 *   }
 * }
 * ```
 */
export abstract class CustomPass {
  /** Unique name for this pass */
  readonly name: string;
  
  /** Where in the pipeline this pass executes */
  readonly injectionPoint: InjectionPoint;
  
  /** Whether this pass is enabled */
  enabled = true;
  
  /** Execution order within the same injection point (lower = earlier) */
  order = 0;
  
  /** Internal state */
  private _initialized = false;
  
  constructor(name: string, injectionPoint: InjectionPoint) {
    this.name = name;
    this.injectionPoint = injectionPoint;
  }
  
  /**
   * Initialize pass resources. Called once before first execution.
   * Override to create GPU resources (textures, buffers, pipelines).
   */
  initialize(ctx: IRenderContext): void {
    if (this._initialized) return;
    this.onInitialize(ctx);
    this._initialized = true;
  }
  
  /**
   * Execute the pass.
   * @param context Current render context with all required resources
   */
  execute(context: CustomPassContext): void {
    if (!this.enabled) return;
    if (!this._initialized) {
      this.initialize(context.ctx);
    }
    this.onExecute(context);
  }
  
  /**
   * Cleanup pass resources. Called when pass is destroyed.
   */
  dispose(ctx: IRenderContext): void {
    if (!this._initialized) return;
    this.onDispose(ctx);
    this._initialized = false;
  }
  
  /**
   * Check if pass is initialized
   */
  get isInitialized(): boolean {
    return this._initialized;
  }
  
  // ============================================
  // Override these in subclasses
  // ============================================
  
  /**
   * Override to create GPU resources
   */
  protected onInitialize(ctx: IRenderContext): void {
    // Default: no-op
  }
  
  /**
   * Override to implement pass logic
   */
  protected abstract onExecute(context: CustomPassContext): void;
  
  /**
   * Override to cleanup GPU resources
   */
  protected onDispose(ctx: IRenderContext): void {
    // Default: no-op
  }
}

